import { useEffect, useRef } from 'react';

import { getWindow } from '@change-corgi/core/window';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionGuidesCategoryPageData } from '#src/app/pages/petitionGuides/category/details/pageContext';

export type Article = {
	slug: string;
	heading: string;
	heroImage: {
		title: string;
		url: string;
		description: string | null;
	} | null;
};

type Result = ModelHookResult<
	{
		articles: readonly Article[];
		containerRef: React.RefObject<HTMLElement | null>;
		containerAriaLabel: string;
	},
	{
		trackArticleClick: (slug: string) => void;
	}
>;

/**
 * The articles list is the crawler-facing contract for this page: it must be returned
 * completely untouched for the current page — no `.slice()`, no `.filter()`, no cap — so
 * every article on that page is present as a real anchor in the initial SSR HTML for JS-less
 * crawlers. `CategoryPagination`'s anchors carry the chain across pages.
 *
 * On a page change, focus moves to `containerRef` (so a control that disappears after being
 * clicked, e.g. "Next" on the last page, doesn't drop focus to `<body>`) and the page jumps to
 * the top — duplicated with the click-time jump in `useCategoryPagination` so it still happens
 * once the new articles are actually on screen. Both scrolls are instant, not smooth, so they
 * don't animate against each other; `preventScroll` stops `focus()` triggering a third.
 * `previousPageRef` compares values rather than latching a boolean, since `<StrictMode>`'s
 * double-invoked effects would otherwise fire this on first render too.
 */
export function useArticleList(): Result {
	const {
		data: { articles, page: currentPage },
	} = usePetitionGuidesCategoryPageData();
	const track = useTracking();
	const { translate } = useI18n();

	const containerRef = useRef<HTMLElement>(null);
	const previousPageRef = useRef(currentPage);
	useEffect(() => {
		if (previousPageRef.current === currentPage) return;
		previousPageRef.current = currentPage;
		getWindow().scrollTo({ top: 0, behavior: 'instant' });
		containerRef.current?.focus({ preventScroll: true });
	}, [currentPage]);

	return {
		data: {
			articles,
			containerRef,
			containerAriaLabel: translate('corgi.petition_guides_category_page.article_list.aria_label'),
		},
		actions: {
			trackArticleClick: (slug) => {
				void track('petition_guides_category_page_article_click', {
					slug,
					source_location: 'petition_guides_category',
				});
			},
		},
	};
}
